import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const SCORE = { HOT:"bg-rose-100 text-rose-800 border-rose-200", HIGH:"bg-orange-100 text-orange-800 border-orange-200", MEDIUM:"bg-amber-100 text-amber-800 border-amber-200", LOW:"bg-slate-100 text-slate-700 border-slate-200" };
const STAGES = ["new","contacted","interested","qualified","follow_up","site_visit","negotiation","won","lost"];
const STAGE_LABEL = { new:"New", contacted:"Contacted", interested:"Interested", qualified:"Qualified", follow_up:"Follow-up", site_visit:"Site visit", negotiation:"Negotiation", won:"Won", lost:"Lost" };

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [view, setView] = useState("kanban");
  const nav = useNavigate();
  const { user } = useAuth();
  const prefix = user?.role === "sales_associate" ? "/sales" : "";

  const load = async () => setLeads((await api.get("/leads")).data);
  useEffect(() => { load(); }, []);

  const move = async (lead, status, e) => {
    if (e) e.stopPropagation();
    try { await api.put(`/leads/${lead.id}`, { status }); toast.success(`Moved to ${STAGE_LABEL[status]}`); load(); }
    catch { toast.error("Failed"); }
  };
  const open = (l) => nav(`${prefix}/leads/${l.id}`);

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-full">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Leads CRM</h1>
          <p className="text-sm text-slate-500">{leads.length} leads across your pipeline</p>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          <button onClick={()=>setView("kanban")} className={`px-3 py-1 text-sm rounded ${view==="kanban" ? "bg-white shadow-sm font-medium" : "text-slate-600"}`} data-testid="view-kanban">Kanban</button>
          <button onClick={()=>setView("list")} className={`px-3 py-1 text-sm rounded ${view==="list" ? "bg-white shadow-sm font-medium" : "text-slate-600"}`} data-testid="view-list">List</button>
        </div>
      </div>

      {view === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin">
          {STAGES.map((s) => {
            const cards = leads.filter(l => l.status === s);
            return (
              <div key={s} className="w-72 shrink-0 bg-slate-100/80 rounded-xl p-3 border border-slate-200/80" data-testid={`kanban-column-${s}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold text-sm text-slate-700">{STAGE_LABEL[s]}</div>
                  <Badge variant="outline" className="bg-white text-[10px]">{cards.length}</Badge>
                </div>
                <div className="space-y-2 max-h-[calc(100vh-14rem)] overflow-y-auto scrollbar-thin">
                  {cards.map((l) => (
                    <Card key={l.id} className="p-3 bg-white border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={()=>open(l)} data-testid={`lead-card-${l.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{l.name}</div>
                          <div className="text-[11px] text-slate-500 font-mono">{l.phone}</div>
                        </div>
                        <Badge className={`${SCORE[l.score]} text-[10px]`}>{l.score}</Badge>
                      </div>
                      {l.requirement && <div className="text-xs text-slate-600 mt-2 truncate">🏠 {l.requirement}</div>}
                      {l.budget && <div className="text-xs text-slate-600">💰 {l.budget}</div>}
                      {l.location && <div className="text-xs text-slate-600">📍 {l.location}</div>}
                      <div className="mt-2 pt-2 border-t border-slate-100" onClick={(e)=>e.stopPropagation()}>
                        <Select value={l.status} onValueChange={(v)=>move(l,v)}>
                          <SelectTrigger className="h-7 text-xs" data-testid={`lead-stage-select-${l.id}`}><SelectValue/></SelectTrigger>
                          <SelectContent className="bg-white">
                            {STAGES.map(x => <SelectItem key={x} value={x}>{STAGE_LABEL[x]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="p-3 text-left">Name</th><th className="p-3 text-left">Phone</th><th className="p-3 text-left">Requirement</th>
                  <th className="p-3 text-left">Budget</th><th className="p-3 text-left">Score</th><th className="p-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(l => (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={()=>open(l)} data-testid={`lead-row-${l.id}`}>
                    <td className="p-3 font-medium">{l.name}</td>
                    <td className="p-3 font-mono text-xs">{l.phone}</td>
                    <td className="p-3 text-slate-600">{l.requirement || "—"}</td>
                    <td className="p-3 text-slate-600">{l.budget || "—"}</td>
                    <td className="p-3"><Badge className={SCORE[l.score]}>{l.score}</Badge></td>
                    <td className="p-3"><Badge variant="outline">{STAGE_LABEL[l.status]}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
